/**
 * Single HTTP entry point for the app.
 *
 * The access token is held in memory only (never localStorage) and refreshed
 * transparently on a 401 using the httpOnly refresh cookie, so a reload keeps
 * the session without exposing a long-lived token to scripts (FR-1.3, FR-1.5).
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** True when the field-level messages from zod validation are present. */
  get fieldErrors(): Record<string, string> | null {
    return this.details && typeof this.details === 'object'
      ? (this.details as Record<string, string>)
      : null;
  }
}

let accessToken: string | null = null;
let refreshPromise: Promise<string | null> | null = null;
const listeners = new Set<(token: string | null) => void>();

export function setAccessToken(token: string | null): void {
  accessToken = token;
  for (const listener of listeners) listener(token);
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function onAccessTokenChange(listener: (token: string | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

async function readError(response: Response): Promise<ApiError> {
  let code = 'error';
  let message = response.statusText || 'Request failed';
  let details: unknown;
  try {
    const body = (await response.json()) as { error?: { code?: string; message?: string; details?: unknown } };
    if (body.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      details = body.error.details;
    }
  } catch {
    // Non-JSON error body (proxy error page, network failure) — keep the status text.
  }
  return new ApiError(response.status, code, message, details);
}

/** Exchanges the refresh cookie for a new access token; de-duplicated. */
export async function refreshSession(): Promise<string | null> {
  refreshPromise ??= (async () => {
    try {
      const response = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
      if (!response.ok) {
        setAccessToken(null);
        return null;
      }
      const body = (await response.json()) as { accessToken: string };
      setAccessToken(body.accessToken);
      return body.accessToken;
    } catch {
      setAccessToken(null);
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Sent as multipart/form-data instead of JSON. */
  form?: FormData;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Internal: prevents an infinite refresh loop. */
  retryOnUnauthorized?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = path.startsWith('/api') ? path : `/api${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, form, query, signal, retryOnUnauthorized = true } = options;
  const headers: Record<string, string> = { ...options.headers };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (body !== undefined && !form) headers['Content-Type'] = 'application/json';

  const response = await fetch(buildUrl(path, query), {
    method,
    headers,
    credentials: 'include',
    signal,
    body: form ?? (body !== undefined ? JSON.stringify(body) : undefined),
  });

  if (response.status === 401 && retryOnUnauthorized) {
    const refreshed = await refreshSession();
    if (refreshed) {
      return request<T>(path, { ...options, retryOnUnauthorized: false });
    }
  }

  if (!response.ok) throw await readError(response);
  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) return (await response.text()) as unknown as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', form }),
};

/**
 * POSTs and reads the reply as server-sent events, calling `onEvent` with each
 * JSON payload as it arrives. Used for replies that stream in (the companion).
 * Errors before the stream opens are thrown as an `ApiError`, exactly like
 * `request`, so callers can treat validation and limits the usual way.
 */
export async function streamEvents<E>(
  path: string,
  body: unknown,
  onEvent: (event: E) => void,
  signal?: AbortSignal,
): Promise<void> {
  const send = (token: string | null) =>
    fetch(buildUrl(path), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
      signal,
      body: JSON.stringify(body),
    });

  let response = await send(accessToken);
  if (response.status === 401) {
    const refreshed = await refreshSession();
    if (refreshed) response = await send(refreshed);
  }
  if (!response.ok) throw await readError(response);
  if (!response.body) throw new ApiError(500, 'no_stream', 'The server did not send a reply');

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += value;
    // Events are separated by a blank line; a partial one waits for more.
    let boundary = buffer.indexOf('\n\n');
    while (boundary !== -1) {
      const chunk = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const data = chunk
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trimStart())
        .join('\n');
      if (data) onEvent(JSON.parse(data) as E);
      boundary = buffer.indexOf('\n\n');
    }
  }
}

/**
 * Fetches an authenticated endpoint as a Blob — for in-page previews, where an
 * <img> or <iframe> cannot send the bearer token itself.
 */
export async function fetchBlob(path: string, query?: RequestOptions['query'], signal?: AbortSignal): Promise<Blob> {
  const send = (token: string | null) =>
    fetch(buildUrl(path, query), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
      signal,
    });
  let response = await send(accessToken);
  if (response.status === 401) {
    const refreshed = await refreshSession();
    if (refreshed) response = await send(refreshed);
  }
  if (!response.ok) throw await readError(response);
  return response.blob();
}

/** Triggers a browser download of an authenticated endpoint (CSV exports). */
export async function downloadFile(path: string, query?: RequestOptions['query']): Promise<void> {
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  let response = await fetch(buildUrl(path, query), { headers, credentials: 'include' });
  if (response.status === 401) {
    const refreshed = await refreshSession();
    if (refreshed) {
      response = await fetch(buildUrl(path, query), {
        headers: { Authorization: `Bearer ${refreshed}` },
        credentials: 'include',
      });
    }
  }
  if (!response.ok) throw await readError(response);

  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') ?? '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = match?.[1] ?? 'export.csv';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
